import { IntlShape } from 'react-intl';
import { EWorkoutStatus, WorkoutStatus } from 'types/enums/workout-status';

// ==================== FORMATEO DE FECHAS ====================

/**
 * Convierte un día-calendario que viene de la API en un Date local del MISMO día.
 *
 * El droplet corre en UTC y las columnas `datetime` guardan los días-calendario a
 * medianoche, así que llegan como "2026-06-15T00:00:00.000Z". Leerlos con los getters
 * locales desde ART (UTC-3) devuelve el 14, no el 15: es el bug de "me resta un día".
 *
 * Por eso se leen las partes en UTC y se reconstruye el Date en local, que además deja
 * la hora en 00:00 y permite compararlo contra hoy.
 *
 * Ojo: es para fechas que representan UN DÍA (inicio de suscripción, cobro, sesión,
 * fechas importantes). Para timestamps reales —cuándo se registró un pago, a qué hora
 * entrenó— hay que usar el Date tal cual, que ahí la hora importa.
 *
 * El calendario ya resolvía esto por su lado con `timeZone="UTC"` en FullCalendar.
 */
export const toCalendarDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const src = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(src.getTime())) return null;
  return new Date(src.getUTCFullYear(), src.getUTCMonth(), src.getUTCDate());
};

/** Formatea un día-calendario de la API sin correrlo por el timezone del browser. */
export const formatCalendarDate = (
  value: string | Date | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions
): string => {
  const date = toCalendarDate(value);
  if (!date) return '';
  return date.toLocaleDateString(locale, options);
};

export const formatDate = (value: string | Date | null | undefined): string => {
  if (!value) return '';
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Los meses son indexados desde 0
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatDateToApi = (date: Date): string => {
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};

export const formatRelativeDate = (date: string | Date | null | undefined, intl: IntlShape): string => {
  if (!date) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDate = new Date(date);
  inputDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - inputDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return intl.formatMessage({ id: 'coach.home.today' });
  } else if (diffDays === 1) {
    return intl.formatMessage({ id: 'coach.home.yesterday' });
  } else {
    return intl.formatMessage({ id: 'coach.home.daysAgo' }, { days: diffDays });
  }
};

export const getDayMonthYear = (session: { sessionDate: string | Date }): Date => {
  const sessionDate = new Date(session.sessionDate);
  const year = sessionDate.getFullYear();
  const month = sessionDate.getMonth(); // 0-based
  const day = sessionDate.getDate();
  return new Date(year, month, day);
};

// ==================== YOUTUBE ====================

export const isValidYouTubeUrl = (url: string): boolean => {
  const regex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/(watch\?v=|embed\/|shorts\/|v\/|.+)?$/;
  return regex.test(url);
};

export const extractYouTubeVideoId = (url: string | null | undefined): string | null => {
  if (!url) return null;

  let videoId = '';

  if (url.includes('youtube.com/watch?v=')) {
    videoId = url.split('v=')[1]?.split('&')[0] || '';
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('.be/')[1]?.split('?')[0] || '';
  } else if (url.includes('youtube.com/shorts/')) {
    videoId = url.split('/shorts/')[1]?.split('?')[0] || '';
  } else if (url.includes('youtube.com/embed/')) {
    videoId = url.split('/embed/')[1]?.split('?')[0] || '';
  } else {
    // Otros casos posibles
    const match = url.match(/v=([^&]+)/);
    if (match?.[1]) {
      videoId = match[1];
    }
  }

  return videoId || null; // Retorna null si no se encuentra un videoId
};

export const getYouTubeThumbnail = (url: string): string => {
  console.log('url', url);
  const videoId = extractYouTubeVideoId(url);
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

// ==================== WORKOUTS ====================

interface WorkoutWithSession {
  status: WorkoutStatus;
  trainingSession: {
    sessionDate: string | Date;
  };
}

export const sortBySessionDate = <T extends WorkoutWithSession>(workouts: T[]): T[] => {
  return workouts.sort((a, b) => {
    const dateA = new Date(a.trainingSession.sessionDate);
    const dateB = new Date(b.trainingSession.sessionDate);
    return dateA.getTime() - dateB.getTime();
  });
};

export function updateStatus<T extends WorkoutWithSession>(workouts: T[]): T[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  workouts.forEach((workout) => {
    const sessionDate = new Date(workout.trainingSession.sessionDate);
    const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());

    if (workout.status === EWorkoutStatus.PENDING) {
      if (sessionDay < today) {
        workout.status = EWorkoutStatus.EXPIRED;
      } else if (sessionDay.getTime() === today.getTime()) {
        workout.status = EWorkoutStatus.PENDING;
      }
    }
  });

  return workouts;
}

export const getSeverity = (status: WorkoutStatus | 'renewal'): 'danger' | 'success' | 'info' | 'warning' | null => {
  switch (status) {
    case EWorkoutStatus.EXPIRED:
      return 'danger';
    case EWorkoutStatus.COMPLETED:
      return 'success';
    case EWorkoutStatus.PENDING:
      return 'info';
    case EWorkoutStatus.CANCELLED:
      return 'warning';
    default:
      return null;
  }
};

// ==================== VALIDACIONES ====================

interface ValidationResult {
  isValid: boolean;
  message: string;
}

export const validateDates = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  intl: IntlShape
): ValidationResult => {
  if (!startDate) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.selectStartDate' })
    };
  }
  if (!endDate) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.selectEndDate' })
    };
  }
  if (new Date(startDate) > new Date(endDate)) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.startDateAfterEndDate' })
    };
  }
  return { isValid: true, message: '' };
};

interface StudentDetails {
  name: string;
  email: string;
  fitnessGoal: string[];
  activityLevel: string;
  birthdate: Date | string;
  gender: string;
  height: number;
  weight: number;
}

export const validateStudentDetails = (
  { name, email, fitnessGoal, activityLevel, birthdate, gender, height, weight }: StudentDetails,
  intl: IntlShape
): ValidationResult => {
  if (!name) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.nameRequired' })
    };
  }
  if (!email) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.emailRequired' })
    };
  }
  if (!fitnessGoal || fitnessGoal.length === 0) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.fitnessGoalRequired' })
    };
  }
  if (!activityLevel) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.activityLevelRequired' })
    };
  }
  if (!birthdate) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.birthdateRequired' })
    };
  }

  const today = new Date();
  const minDate = new Date();
  minDate.setFullYear(today.getFullYear() - 10);

  if (new Date(birthdate) > today) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.birthdateInFuture' })
    };
  }
  if (new Date(birthdate) > minDate) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.ageRequirement' })
    };
  }

  if (!gender) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.genderRequired' })
    };
  }
  if (height < 100 || height > 250) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.heightRange' })
    };
  }
  if (weight < 30 || weight > 300) {
    return {
      isValid: false,
      message: intl.formatMessage({ id: 'error.weightRange' })
    };
  }

  return { isValid: true, message: '' };
};

// ==================== AUTH ====================

export const getToken = (): string | null => {
  const token = localStorage.getItem('token');
  return token;
};

export const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`
  };
};

// ==================== CALENDAR EVENTS ====================

/**
 * Updates the status of a workout instance based on the session date
 * @param workout - The workout instance object
 * @param session - The training session object
 * @returns Updated status string
 */
export const updateStatusLocal = (workout: { status: string }, session: { sessionDate: string | Date }): string => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sessionDate = new Date(session.sessionDate);
  const sessionDay = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
  if (workout.status === 'pending') {
    if (sessionDay < today) {
      return 'expired';
    } else if (sessionDay.getTime() === today.getTime()) {
      return 'current';
    }
  }
  return workout.status;
};

/**
 * Maps a training session to a calendar event
 * @param session - The training session object
 * @param workoutInstance - The workout instance (optional)
 * @param cycleName - The cycle name (optional, for sessions without workout instances)
 * @returns Calendar event object
 */
export const mapSessionToCalendarEvent = (
  session: {
    sessionDate: string | Date;
    id: number;
    sessionMode?: string;
    location?: string;
    sessionTime?: string;
    contactMethod?: string;
    notes?: string;
  },
  workoutInstance:
    | {
        instanceName?: string;
        workoutTemplate?: { planName?: string };
        id: number;
        status: string;
      }
    | null
    | undefined = null,
  cycleName: string | null = null
): {
  title: string;
  start: string;
  allDay: boolean;
  extendedProps: {
    status?: string;
    workoutInstanceId?: number;
    sessionId: number;
    cycle?: string | null;
    sessionMode?: string;
    location?: string;
    sessionTime?: string;
    contactMethod?: string;
    notes?: string;
  };
} => {
  // Normalize sessionDate to string
  const sessionDateStr =
    typeof session.sessionDate === 'string' ? session.sessionDate : session.sessionDate.toISOString();
  const startDate = sessionDateStr.split('T')[0] || '';

  if (workoutInstance) {
    // Update status if needed
    const status = updateStatusLocal(workoutInstance, session);
    return {
      title: workoutInstance.instanceName || workoutInstance.workoutTemplate?.planName || 'Sin título',
      start: startDate,
      allDay: true,
      extendedProps: {
        status: status,
        workoutInstanceId: workoutInstance.id,
        sessionId: session.id,
        // #23 ítem 3: el ciclo viaja también cuando la sesión YA tiene
        // entrenamiento, si no el coach no sabe sobre cuál está operando.
        cycle: cycleName,
        sessionMode: session.sessionMode,
        location: session.location,
        sessionTime: session.sessionTime,
        contactMethod: session.contactMethod,
        notes: session.notes
      }
    };
  } else {
    // Session without workout instance
    return {
      title: 'no title',
      start: startDate,
      allDay: true,
      extendedProps: {
        sessionId: session.id,
        cycle: cycleName,
        sessionMode: session.sessionMode,
        location: session.location,
        sessionTime: session.sessionTime,
        contactMethod: session.contactMethod,
        notes: session.notes
      }
    };
  }
};

export function toQueryString(params: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;

    if (v instanceof Date) {
      qs.append(k, v.toISOString());
      continue;
    }
    if (Array.isArray(v)) {
      v.forEach((item) => {
        if (item !== undefined && item !== null) qs.append(k, String(item));
      });
      continue;
    }
    // objetos simples → JSON (opcional)
    if (typeof v === 'object') {
      qs.append(k, JSON.stringify(v));
      continue;
    }
    qs.append(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
